/**
 * アプリ本体の表示言語（英語UI）。
 *
 * 方式＝「日本語の原文をそのままキーにして EN 辞書を引く」:
 *   - 文字列はコンポーネント内に日本語のまま残り、`t("ホーム")` と包むだけ
 *   - EN 辞書（i18n.en.ts）に無い文字列は原文（日本語）へフォールバック＝
 *     訳し漏れで画面が壊れない・未訳箇所が実画面でそのまま見えるので発見も早い
 *   - 既定 locale は ja ＝ locale 未指定（テスト・スクショ/動画ハーネス・非埋め込み）では
 *     従来どおり日本語で描画される（既存テストが 1 本も変わらない前提はここ）
 *
 * locale は Shopify admin が埋め込み iframe URL に付ける `locale` パラメータ
 * （例 "en-US"）か店ごとの手動選択で決まり、ページ読込中は不変。
 */

import { EN } from "./i18n.en";
import { DE } from "./i18n.de";
import { DE_MANUAL } from "./i18n.de.manual";
import { normalizeAdminLocale, isAdminLocale, type AdminLocale } from "./adminLocales";

export type Locale = AdminLocale;
export type LocalePreference = Locale | "auto";

const additionalDictionaries = import.meta.glob<Record<string, string>>("./i18n.*.json", {
  import: "default",
});
let loadedDictionary: Record<string, string> | null = null;

/** 追加言語の辞書だけを取得し、初期表示のバンドルを肥大化させない。 */
export async function loadLocaleDictionary(locale: Locale): Promise<boolean> {
  if (locale === "ja" || locale === "en" || locale === "de") {
    loadedDictionary = null;
    return true;
  }
  const load = additionalDictionaries[`./i18n.${locale}.json`];
  if (!load) {
    loadedDictionary = null;
    return false;
  }
  const dictionary = await load();
  if (appLocale !== locale) return false;
  loadedDictionary = dictionary;
  return true;
}

let appLocale: Locale = "ja";
let volatilePreference: { shop: string | null; value: LocalePreference } | null = null;
const PREFERENCE_PREFIX = "ub-ui-locale:";
const ADMIN_LOCALE_PREFIX = "ub-admin-locale:";
const entryShop = typeof window === "undefined"
  ? null
  : new URLSearchParams(window.location.search).get("shop");

function shopFromSearch(search: string): string | null {
  const params = new URLSearchParams(search);
  const shop = (
    params.get("shop") ??
    (typeof window === "undefined" ? null : window.shopify?.config?.shop) ??
    entryShop ??
    ""
  )
    .trim()
    .toLowerCase();
  return /^(?=.{1,63}\.myshopify\.com$)[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.myshopify\.com$/.test(
    shop,
  )
    ? shop
    : null;
}

export function localePreference(search: string): LocalePreference {
  const shop = shopFromSearch(search);
  if (volatilePreference?.shop === shop) return volatilePreference.value;
  if (!shop || typeof window === "undefined") return "auto";
  try {
    const saved = window.localStorage.getItem(`${PREFERENCE_PREFIX}${shop}`);
    return saved && isAdminLocale(saved) ? saved : "auto";
  } catch {
    return "auto";
  }
}

export function setLocalePreference(
  search: string,
  preference: LocalePreference,
): boolean {
  const shop = shopFromSearch(search);
  volatilePreference = { shop, value: preference };
  if (!shop || typeof window === "undefined") return false;
  try {
    const key = `${PREFERENCE_PREFIX}${shop}`;
    if (preference === "auto") window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, preference);
    return true;
  } catch {
    return false;
  }
}

/** 未指定のlocal環境は日本語、未対応の管理画面言語は英語へ倒す。 */
export function detectLocale(search: string): Locale {
  const raw = new URLSearchParams(search).get("locale") ?? "";
  if (!raw) return "ja";
  return normalizeAdminLocale(raw);
}

/** アプリ起動時（main.tsx）に1回だけ呼ぶ。 */
export function initLocale(search: string): Locale {
  const preference = localePreference(search);
  const shop = shopFromSearch(search);
  const params = new URLSearchParams(search);
  let adminLocale = detectLocale(search);
  if (shop && !params.has("locale")) adminLocale = "en";
  if (shop && typeof window !== "undefined") {
    try {
      const key = `${ADMIN_LOCALE_PREFIX}${shop}`;
      if (params.get("billing_pending") === "1") {
        const saved = window.sessionStorage.getItem(key);
        if (saved && isAdminLocale(saved)) adminLocale = saved;
      } else if (params.has("locale")) {
        window.sessionStorage.setItem(key, adminLocale);
      }
    } catch {
      // sessionStorage が使えない場合は現在の URL だけで決める。
    }
  }
  return setCurrentLocale(preference === "auto" ? adminLocale : preference);
}

/** 言語切替は保存機能が使えないブラウザでも、その場で反映する。 */
export function setCurrentLocale(locale: Locale): Locale {
  appLocale = locale;
  loadedDictionary = null;
  // 画面の文言だけでなく、支援技術・ブラウザ翻訳・スペルチェックにも現在言語を
  // 伝える。index.html の固定値を残すと英語 UI でも日本語文書として読まれる。
  if (typeof document !== "undefined") {
    document.documentElement.lang = appLocale;
    document.documentElement.dir = appLocale === "ar" || appLocale === "he" || appLocale === "ur" ? "rtl" : "ltr";
  }
  return appLocale;
}

export function currentLocale(): Locale {
  return appLocale;
}

/** Backendのrequest error辞書はJA/ENのみ。DE画面では英語を返して日本語混入を避ける。 */
export function requestLocale(): "ja" | "en" {
  return appLocale === "ja" ? "ja" : "en";
}

/** テスト専用: locale を直接差し替える（本番コードからは呼ばない）。 */
export function setLocaleForTesting(locale: Locale): void {
  setCurrentLocale(locale);
}

export function resetLocalePreferenceForTesting(): void {
  volatilePreference = null;
}

/**
 * 日本語の原文を表示言語の文字列へ解決する。
 *
 * `vars` を渡すと原文中の `{name}` を差し込む。数値や件数を文の途中に挟む場合に
 * 文を分割してはいけない＝英語は語順が違うので、断片を並べると訳が破綻する。
 *   t("残り {n} 件", { n: 3 })  →  ja: "残り 3 件" / en: "3 remaining"
 */
export function t(ja: string, vars?: Record<string, string | number>): string {
  const base = appLocale === "ja" ? ja
    : appLocale === "de" ? (DE_MANUAL[ja] ?? DE[ja] ?? EN[ja] ?? ja)
    : appLocale === "en" ? (EN[ja] ?? ja)
    : (loadedDictionary?.[ja] ?? EN[ja] ?? ja);
  if (!vars) {
    return base;
  }
  return Object.entries(vars).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    base,
  );
}
